import { Component, ViewEncapsulation, OnDestroy, HostBinding } from '@angular/core';
import { FormControl, Validators, FormGroup, ValidatorFn, AbstractControl, ReactiveFormsModule } from '@angular/forms';
import { firstValueFrom, Subscription } from 'rxjs';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { ActivatedRoute } from '@angular/router';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { MatLegacyFormFieldModule } from '@angular/material/legacy-form-field';
import { NgIf, NgFor, NgClass } from '@angular/common';

import { simpleTransitions } from '../../shared/animations/simple-transitions';
import { User } from '../../models/user';
import { UserService } from '../../services/user.service';
import { LoadingSpinnerComponent } from '../utils/loading-spinner.component';

import { ErrorDialogComponent } from './dialogs/error.component';
import { PrivacyPolicyDialogComponent } from './dialogs/privacy-policy.component';
import { FaqDialogComponent } from './dialogs/faq.component';

const passwordPatternLowerCase = /[a-z]/;
const passwordPatternUpperCase = /[A-Z]/;
const passwordPatternDigit = /\d/;
const passwordPatternSpecialChars = /[!"#$%&'()*+,./:;<=>?@[\\\]^_`{|}~-]/;
const passwordMinChars = 15;

function namedValidator(validator: ValidatorFn, name: string): ValidatorFn {
	return control => {
		const errors = validator(control);
		if (!errors) {
			return null;
		}
		return Object.fromEntries(Object.entries(errors).map(([key, value]) => [key + name, value]));
	};
}

const confirmPasswordValidator = (group: AbstractControl) => {
	const passwordFormControl = group.get('password');
	const confirmPasswordFormControl = group.get('confirmPassword');
	if (!passwordFormControl || !confirmPasswordFormControl) {
		return null;
	}
	const passwordValue = <string>passwordFormControl.value;
	const confirmPasswordValue = <string>confirmPasswordFormControl.value;
	return passwordValue !== confirmPasswordValue
		? { noMatching: 'Invalid confirm password' }
		: null;
};

@Component({
	selector: 'copo-confirm-password-legacy',
	templateUrl: './confirm-password-legacy.component.html',
	animations: simpleTransitions,
	encapsulation: ViewEncapsulation.None,
	standalone: true,
	imports: [
		NgIf,
		TranslateModule,
		ReactiveFormsModule,
		NgFor,
		// eslint-disable-next-line deprecation/deprecation
		MatLegacyFormFieldModule,
		NgClass,
		LoadingSpinnerComponent
	]
})
export class ConfirmPasswordLegacyComponent implements OnDestroy {
	@HostBinding('class.copo-login-legacy') readonly componentClass = true;
	@HostBinding('class.full-height') fullHeight = true;

	submitted = false;
	submitting = false;
	passwordPatternLowerCase = namedValidator(Validators.pattern(passwordPatternLowerCase), 'LowerCase');
	passwordPatternUpperCase = namedValidator(Validators.pattern(passwordPatternUpperCase), 'UpperCase');
	passwordPatternDigit = namedValidator(Validators.pattern(passwordPatternDigit), 'Digit');
	passwordPatternSpecialChars = namedValidator(Validators.pattern(passwordPatternSpecialChars), 'SpecialChars');
	passwordMinChars = Validators.minLength(passwordMinChars);
	passwordPatternStr = `(?=.*${
		passwordPatternLowerCase.source
	})(?=.*${
		passwordPatternUpperCase.source
	})(?=.*${
		passwordPatternDigit.source
	})(?=.*${
		passwordPatternSpecialChars.source
	}).{${
		passwordMinChars
	},}`;

	confirmPasswordFormGroup = new FormGroup(
		{
			firstName: new FormControl({ value: '', disabled: true }, { validators: Validators.required }),
			lastName: new FormControl({ value: '', disabled: true }, { validators: Validators.required }),
			gender: new FormControl({ value: '', disabled: true }, { validators: Validators.required }),
			language: new FormControl({ value: '', disabled: true }, { validators: Validators.required }),
			email: new FormControl(
				{ value: '', disabled: true },
				[Validators.required, Validators.pattern('^[a-zA-Z0-9._\'-]+@[a-zA-Z0-9.-]{2,}[.][a-zA-Z]{2,}$')]
			),
			username: new FormControl({ value: '', disabled: true }, { validators: Validators.required }),
			password: new FormControl('', [
				Validators.required,
				this.passwordPatternLowerCase,
				this.passwordPatternUpperCase,
				this.passwordPatternDigit,
				this.passwordPatternSpecialChars,
				this.passwordMinChars
			]),
			confirmPassword: new FormControl('', [Validators.required])
		},
		confirmPasswordValidator
	);

	errorStatus: string | undefined;

	sessionToken: string | undefined;
	userSub: Subscription | undefined;
	isLoading = true;
	user: User | undefined;

	lang: string;
	langsDisplayed = false;
	displayLangsTimeout: number | undefined;
	langSub: Subscription | undefined;

	readonly genders = [
		{ value: 'mr', display: 'confirm-password-legacy.mr' },
		{ value: 'mrs', display: 'confirm-password-legacy.mrs' },
		{ value: 'ms', display: 'confirm-password-legacy.ms' }
	];

	readonly languages = [
		{ value: 'de', display: 'confirm-password-legacy.deutsch' },
		{ value: 'en', display: 'confirm-password-legacy.english' },
		{ value: 'es', display: 'confirm-password-legacy.spanish' },
		{ value: 'fr', display: 'confirm-password-legacy.french' },
		{ value: 'it', display: 'confirm-password-legacy.italian' },
		{ value: 'pl', display: 'confirm-password-legacy.polish' }
	];

	constructor(
		private readonly route: ActivatedRoute,
		private readonly http: HttpClient,
		private readonly translate: TranslateService,
		private readonly userService: UserService,
		// eslint-disable-next-line deprecation/deprecation
		public dialog: MatDialog
	) {
		const params = <Record<string, string | undefined>> this.route.snapshot.queryParams;
		this.sessionToken = params['session_token'];

		this.lang = this.translate.currentLang;

		if (!this.sessionToken) {
			this.dialog.open(ErrorDialogComponent, {
				width: '350px',
				panelClass: 'legacy-dialog',
				data: { errorStatus: 'Invalid link' }
			});
			return;
		}

		this.userSub = this.userService.getUser(this.sessionToken)
			.subscribe({
				next: (data: User) => {
					this.isLoading = false;
					this.confirmPasswordFormGroup.patchValue({
						firstName: data.firstName,
						lastName: data.lastName,
						gender: data.gender,
						language: data.language,
						username: data.username,
						email: data.address.email,
						password: data.password
					});
				},
				error: (err: unknown) => {
					console.error(err);
					this.dialog.open(ErrorDialogComponent, {
						width: '350px',
						panelClass: 'legacy-dialog',
						data: { errorStatus: 'Invalid link' }
					});
				}
			});
	}

	get password() {
		return this.confirmPasswordFormGroup.get('password');
	}

	get confirmPassword() {
		return this.confirmPasswordFormGroup.get('confirmPassword');
	}

	get confirmPasswordUrl() {
		return this.userService.getConfirmPasswordUrl(this.sessionToken);
	}

	get accessUrl() {
		return this.userService.getConnectionUrl();
	}

	get year() {
		return new Date().getFullYear();
	}

	async onSubmit() {
		this.submitted = true;
		if (!this.confirmPasswordFormGroup.valid) {
			return;
		}
		this.errorStatus = undefined;
		this.submitting = true;
		try {
			const password = <string | undefined> this.confirmPasswordFormGroup.get('password')?.value;
			await firstValueFrom(this.http.post(
				this.confirmPasswordUrl.toString(),
				password
			));
			// reenable after 30 seconds (an error probably happened on load)
			location.replace(this.accessUrl.toString());
		} catch (err) {
			this.submitting = false;
			if (!(err instanceof HttpErrorResponse) || err.status !== 401) {
				console.error(err);
				return;
			}
			if (typeof err.error === 'object') {
				this.errorStatus = (<{ status: string }> err.error).status;
				this.errorStatus ||= 'unknown';
				this.openErrorDialog();
			} else {
				console.error(err);
			}
		}
	}

	ngOnDestroy() {
		if (this.langSub) {
			this.langSub.unsubscribe();
		}
		if (this.userSub) {
			this.userSub.unsubscribe();
		}
	}

	setLanguage(lang: string) {
		this.langSub = this.translate.use(lang).subscribe(() => {
			this.lang = lang;
			localStorage.setItem('language', lang);
			this.langsDisplayed = !this.langsDisplayed;
		});
	}

	outDivLangs() {
		this.displayLangsTimeout = setTimeout(
			() => {
				this.langsDisplayed = false;
			},
			5000
		);
	}

	inDivLangs() {
		if (this.displayLangsTimeout !== undefined) {
			clearTimeout(this.displayLangsTimeout);
			this.displayLangsTimeout = undefined;
		}
	}

	openPrivacyPolicyDialog() {
		this.dialog.open(PrivacyPolicyDialogComponent, {
			width: '1000px',
			maxHeight: '100vh',
			maxWidth: '100vw',
			panelClass: 'legacy-dialog'
		});
	}

	openFaqDialog() {
		this.dialog.open(FaqDialogComponent, {
			width: '1000px',
			maxHeight: '100vh',
			maxWidth: '100vw',
			panelClass: 'legacy-dialog'
		});
	}

	openErrorDialog() {
		this.dialog.open(ErrorDialogComponent, {
			width: '350px',
			panelClass: 'legacy-dialog',
			data: { errorStatus: this.errorStatus }
		});
	}
}
